/** "O dia de hoje": prazo hoje + atrasados dos canais com prazo de API. Não usa os filtros da tela (é o número da
 * equipe inteira). O ritmo mira a coleta de cada canal ("Por coleta"); o fim do expediente só vale para "encerrado". */
import { useMemo, useState, type ReactNode } from 'react'
import {
  Alert, Box, Card, CardActionArea, CardContent, Chip, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, useMediaQuery, useTheme,
} from '@mui/material'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { AbrirLista, DadosExpedicao, LinhaColeta, Pedido } from './types'
import {
  COR_SITUACAO, ROTULO_ETAPA, avisoDoDia, doEtapa, fechado, type Etapa, diaMes, jaEmbaladosAoAbrir, linhasAndamento, ordenarColetas, pedidosDaColeta, resumoFaltam,
  rotuloColeta, semPrazoDoCanal, semPrazoPorCanal, semRitmoAinda, statusColeta,
} from './logic'
import { carregarOntem } from './fonte'
import { BarraSegmentada, LegendaSituacoes } from './Barras'

/** Do mais adiantado ao mais atrasado; etapa sem pedido não aparece (sem retido/Decora = igual a antes). */
const ETAPAS_DIA: Etapa[] = ['enviado', 3, 'up_etiqueta', 'up_a_processar', 2, 4, 1, 0]
const DIAS_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']
const OUTFIT = '"Outfit", "IBM Plex Sans", sans-serif'

function Numero({ valor, rotulo, onClick, cor }: { valor: number | string; rotulo: string; onClick?: () => void; cor?: string }) {
  return (
    <CardActionArea onClick={onClick} disabled={!onClick} sx={{ borderRadius: 2, px: 1.25, py: 0.75, bgcolor: 'action.hover' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{rotulo}</Typography>
        <Typography sx={{ font: '800 22px Outfit', color: cor, fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
      </Stack>
    </CardActionArea>
  )
}

/** Card pequeno (Ritmo agora / Precisa de): número + unidade numa linha só e legenda em bloco, cada um com a própria
 * altura de linha (ver relatório da F2: o `font` abreviado zerava o line-height e a legenda era um <span> solto). */
function CardRitmo({ titulo, valor, unidade, legenda, cor, fundo, onClick }: {
  titulo: string; valor: number | string; unidade?: string; legenda: string; cor: string; fundo: string; onClick?: () => void
}) {
  const corpo = (
    <>
      <Typography variant="subtitle2" color="text.secondary" component="div">{titulo}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'nowrap', my: 0.25, color: cor }}>
        <Typography component="span" sx={{ fontFamily: OUTFIT, fontWeight: 800, fontSize: 30, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
        {unidade && <Typography component="span" sx={{ fontFamily: OUTFIT, fontWeight: 700, fontSize: 15, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{unidade}</Typography>}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.35 }}>{legenda}</Typography>
    </>
  )
  const sx = { borderRadius: 3, p: 1.5, bgcolor: fundo, height: '100%' }
  return onClick ? <CardActionArea onClick={onClick} sx={sx}>{corpo}</CardActionArea> : <Box sx={sx}>{corpo}</Box>
}

/** Número clicável dentro da tabela "Por coleta". */
function NumLista({ children, onClick, cor, forte }: { children: ReactNode; onClick: () => void; cor?: string; forte?: boolean }) {
  return (
    <CardActionArea onClick={onClick} sx={{ width: 'auto', display: 'inline-block', borderRadius: 1.5, px: 0.5, color: cor }}>
      <Typography component="span" sx={{ fontWeight: forte ? 800 : 600, fontSize: forte ? 18 : 13, fontFamily: forte ? OUTFIT : undefined, fontVariantNumeric: 'tabular-nums' }}>
        {children}
      </Typography>
    </CardActionArea>
  )
}

/** "Por coleta": tabela no computador; no celular, um bloco por canal (a tabela rolaria e esconderia faltam/situação). */
function PorColeta({ linhas, ritmo, semRitmo, doDia, chaves, abrirLista }: {
  linhas: LinhaColeta[]; ritmo: number; semRitmo: boolean; doDia: Pedido[]; chaves: Set<string>; abrirLista: AbrirLista
}) {
  const celular = useMediaQuery(useTheme().breakpoints.down('sm'))
  if (!linhas.length) return null
  // pela coleta do pedido (Amazon DBA, Site TM Logística têm linha própria), não pelo canal
  const doCanal = (c: LinhaColeta) => pedidosDaColeta(doDia, c.canal, chaves)
  const pend = (c: LinhaColeta) => doCanal(c).filter((p) => !fechado(p))
  const partes = (c: LinhaColeta) => {
    const st = statusColeta(c, ritmo, semRitmo)
    const titulo = `${c.rotulo} · coleta ${c.hora}`
    const detalhe = [c.janela && `janela ${c.janela}`, c.corte && `corte ${c.corte}`].filter(Boolean).join(' · ')
    return {
      hora: (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={{ fontFamily: OUTFIT, fontWeight: 700, fontSize: 16 }}>{c.hora}</Typography>
          {c.fonte === 'estimativa' && <Chip size="small" label="estimativa" variant="outlined" color="warning" sx={{ height: 20, fontSize: 11 }} />}
        </Stack>
      ),
      detalhe: detalhe && <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3 }}>{detalhe}</Typography>,
      faltam: (
        <>
          <NumLista forte onClick={() => abrirLista(`${titulo} · faltam embalar`, pend(c))}>{c.faltam}</NumLista>
          <NumLista onClick={() => abrirLista(`${titulo} · todos`, doCanal(c))} cor="text.secondary">de {c.total}</NumLista>
        </>
      ),
      precisa: c.passou || c.precisa_hora == null ? '—' : `${c.precisa_hora}/h`,
      status: c.passou && c.ficaram > 0
        ? <NumLista onClick={() => abrirLista(`${titulo} · ficaram`, pend(c))} cor="error.main">{st.texto}</NumLista>
        : st.texto,
      corStatus: st.tom === 'erro' ? 'error.main' : st.tom === 'neutro' ? 'text.secondary' : 'success.main',
    }
  }

  if (celular) {
    return (
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {linhas.map((c) => {
          const x = partes(c)
          return (
            <Box key={c.canal} sx={{ py: 0.75 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontWeight: 600 }}>{c.rotulo}</Typography>
                {x.hora}
              </Stack>
              {x.detalhe}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                <Box>{x.faltam}</Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{x.precisa === '—' ? '' : `precisa de ${x.precisa}`}</Typography>
                <Typography variant="body2" component="div" sx={{ fontWeight: 700, color: x.corStatus, ml: 'auto !important' }}>{x.status}</Typography>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    )
  }

  return (
    <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
      <TableHead>
        <TableRow>
          {['Canal', 'Coleta', 'Faltam', 'Precisa de', 'Situação'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'text.secondary' }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {linhas.map((c) => {
          const x = partes(c)
          return (
            <TableRow key={c.canal}>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{c.rotulo}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.hora}{x.detalhe}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.faltam}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{x.precisa}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700, color: x.corStatus }}>{x.status}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export function DiaDeHoje({ dados, abrirLista }: { dados: DadosExpedicao; abrirLista: AbrirLista }) {
  const theme = useTheme()
  const d = dados.dia
  const [erroOntem, setErroOntem] = useState<string | null>(null)
  const doDia = useMemo(() => dados.pedidos.filter((p) => p.no_dia), [dados])
  const pendentes = (f: Pedido['faixa']) => dados.pedidos.filter((p) => p.faixa === f && !fechado(p))
  const ultimaHora = useMemo(() => {
    const fim = new Date(dados.gerado_em).getTime()
    return dados.pedidos.filter((p) => {
      if (!p.embalado_em) return false
      const t = new Date(p.embalado_em).getTime()
      return t > fim - 3600_000 && t <= fim
    })
  }, [dados])
  const coletas = useMemo(() => ordenarColetas(d.por_coleta ?? []), [d])
  // coletas de hoje (as do serviço + as linhas): pedido de coleta sem linha cai no canal, como no serviço
  const chavesColeta = useMemo(() => new Set<string>([...Object.keys(dados.coletas ?? {}), ...coletas.map((c) => c.canal)]), [dados.coletas, coletas])
  const semPrazo = semPrazoPorCanal(d.sem_prazo_por_canal ?? {})
  const comPrazo = new Set<string>(dados.canais_com_prazo ?? [])
  const doCanalSemPrazo = (c: string) => semPrazoDoCanal(dados.pedidos, c, comPrazo)
  const jaEmbalados = useMemo(() => jaEmbaladosAoAbrir(doDia, d.data, dados.expediente.inicio), [doDia, d.data, dados.expediente.inicio])

  const dt = new Date(`${d.data}T12:00:00Z`)
  const titulo = `O dia de hoje · ${DIAS_SEMANA[dt.getUTCDay()]} ${diaMes(d.data)}`
  const fim = dados.expediente.fim
  const fimH = fim.replace(':00', 'h')

  // Andamento hora a hora: a curva parte de 0 (só o que foi visto embalar hoje); os já embalados ao abrir o dia ficam
  // à parte, então a meta da reta ideal é o total menos eles, e ela termina na última coleta com pendência.
  const saldo = d.saldo_inicial ?? 0
  const meta = Math.max(0, d.total - saldo)
  const linhas = linhasAndamento(d.acumulado_hora, dados.expediente.inicio, fim, meta, d.fim_ideal || fim)

  // Aviso: coleta vermelha na tabela manda (citada pelo nome); a previsão contra o fim do expediente fica secundária.
  const semRitmo = semRitmoAinda(d.ritmo_hora, dados.agora_sp, dados.expediente.inicio)
  const aviso = avisoDoDia(d, coletas, fim, semRitmo)

  // "Precisa de": mira a próxima coleta com pendência.
  const prox = d.proxima_coleta
  let precisa: { valor: number | string; legenda: string }
  if (d.encerrado) precisa = { valor: '—', legenda: 'expediente encerrado' }
  else if (d.faltam === 0) precisa = { valor: '—', legenda: 'nada falta embalar' }
  else if (prox && d.precisa_hora != null) precisa = { valor: d.precisa_hora, legenda: `para zerar até ${prox.hora} — ${rotuloColeta(prox.canal)}` }
  else if (!coletas.length && d.precisa_hora != null) precisa = { valor: d.precisa_hora, legenda: `para zerar até ${fimH} (fim do expediente)` }
  else precisa = { valor: '—', legenda: 'as coletas de hoje já passaram' }

  const abrirOntem = async () => {
    setErroOntem(null)
    try {
      const o = await carregarOntem()
      abrirLista(`Prazo em ${diaMes(o.data)} (último dia útil) · ${o.no_prazo} de ${o.total} embalados no prazo`, o.pedidos)
    } catch (e) {
      setErroOntem(e instanceof Error ? e.message : String(e))
    }
  }

  // Enviado (embalado já coletado) separado do Embalado; contagem pela lista do dia, a mesma que a gaveta abre
  const porSit = (s: Etapa) => doEtapa(doDia, s).length
  const abrirSit = (s: Etapa) => abrirLista(`Hoje · ${ROTULO_ETAPA[s]}`, doEtapa(doDia, s))

  return (
    <Card variant="outlined" sx={{ borderWidth: 2, borderColor: 'primary.main' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontSize: 22 }}>{titulo}</Typography>
          <Typography variant="caption" color="text.secondary">
            Pedidos com data máxima de envio hoje + atrasados · não muda com os filtros abaixo
          </Typography>
        </Stack>

        <Grid container spacing={3.5} sx={{ mt: 0.5 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <CardActionArea onClick={() => abrirLista('Pedidos para sair hoje', doDia)} sx={{ width: 'auto', borderRadius: 2, px: 0.5 }}>
                <Typography sx={{ font: '800 64px Outfit', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{d.total}</Typography>
              </CardActionArea>
              <Typography sx={{ font: '600 18px Outfit' }} color="text.secondary">pedidos para sair hoje</Typography>
            </Stack>
            <Stack direction="row" columnGap={2} rowGap={0.5} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Typography sx={{ fontFamily: OUTFIT, fontWeight: 800, fontSize: { xs: 30, sm: 40 }, lineHeight: 1.1, color: COR_SITUACAO[3] }}>{d.pct}% embalado</Typography>
              <CardActionArea onClick={() => abrirLista('Hoje · faltam embalar', doDia.filter((p) => !fechado(p)))}
                sx={{ width: 'auto', borderRadius: 2, px: 0.75 }}>
                <Typography sx={{ fontFamily: OUTFIT, fontWeight: 700, fontSize: { xs: 17, sm: 20 }, lineHeight: 1.2, color: d.faltam ? 'error.dark' : 'text.secondary' }}>
                  {resumoFaltam(d.por_situacao)}
                </Typography>
              </CardActionArea>
            </Stack>
            <Box sx={{ my: 1.25 }}>
              <BarraSegmentada altura={58} total={d.total} situacoes={ETAPAS_DIA} contagem={porSit} onClick={abrirSit} />
            </Box>
            <LegendaSituacoes situacoes={ETAPAS_DIA} contagem={porSit} onClick={abrirSit} tamanho={14} />
            {semPrazo.length > 0 && (
              <Stack direction="row" columnGap={0.5} rowGap={0.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Outros marketplaces, sem data de envio:</Typography>
                {semPrazo.map((x, i) => (
                  <Stack key={x.canal} direction="row" alignItems="center">
                    {i > 0 && <Typography variant="body2" color="text.disabled" sx={{ mr: 0.5 }}>·</Typography>}
                    <CardActionArea onClick={() => abrirLista(`${x.rotulo} · sem data de envio (pendentes)`, doCanalSemPrazo(x.canal))}
                      sx={{ width: 'auto', borderRadius: 1.5, px: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{x.rotulo} {x.n}</Typography>
                    </CardActionArea>
                  </Stack>
                ))}
              </Stack>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Grid container spacing={1.25}>
              <Grid size={6}>
                <CardRitmo titulo="Ritmo agora" valor={d.ritmo_hora} unidade="/hora" legenda="embalados na última hora"
                  cor={theme.palette.primary.main} fundo="#f0fdfa" onClick={() => abrirLista('Embalados na última hora', ultimaHora)} />
              </Grid>
              <Grid size={6}>
                <CardRitmo titulo="Precisa de" valor={precisa.valor} unidade={typeof precisa.valor === 'number' ? '/hora' : undefined}
                  legenda={precisa.legenda} cor={theme.palette.error.dark} fundo="#fef2f2" />
              </Grid>
            </Grid>
            <Alert severity={aviso.tom} sx={{ mt: 1.25, fontWeight: 600 }}>
              {aviso.texto}
              {aviso.secundario && (
                <Typography variant="caption" component="div" sx={{ fontWeight: 400, mt: 0.25, opacity: 0.85 }}>{aviso.secundario}</Typography>
              )}
            </Alert>
          </Grid>
        </Grid>

        {coletas.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Outfit', fontWeight: 700 }}>Por coleta</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              Cada canal tem que estar embalado até a hora da sua coleta · ritmo agora: {d.ritmo_hora}/h · clique nos números para ver os pedidos
            </Typography>
            <PorColeta linhas={coletas} ritmo={d.ritmo_hora} semRitmo={semRitmo} doDia={doDia} chaves={chavesColeta} abrirLista={abrirLista} />
          </Box>
        )}

        <Grid container spacing={3.5} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Outfit', fontWeight: 700 }}>Andamento hora a hora · embalados hoje</Typography>
            <Stack direction="row" columnGap={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <CardActionArea onClick={() => abrirLista('Já embalados ao abrir o dia', jaEmbalados)} sx={{ width: 'auto', borderRadius: 1.5, px: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Já embalados ao abrir o dia: {saldo}</Typography>
              </CardActionArea>
              <Typography variant="caption" color="text.secondary">
                fora da curva · ritmo ideal: {meta} até {d.fim_ideal || fim}
              </Typography>
            </Stack>
            <Box sx={{ height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={linhas} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={theme.palette.divider} vertical={false} />
                  <XAxis dataKey="hora" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                  <Tooltip />
                  <Line dataKey="ideal" name="Ritmo ideal" stroke={theme.palette.text.disabled} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
                  <Line dataKey="real" name="Embalados hoje" stroke={COR_SITUACAO[3]} strokeWidth={3.5} connectNulls={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Outfit', fontWeight: 700, mb: 1 }}>O que vem depois</Typography>
            <Stack spacing={0.75}>
              <Numero rotulo="Amanhã" valor={d.amanha} onClick={() => abrirLista('Amanhã (pendentes)', pendentes('amanha'))} />
              <Numero rotulo="Próximos dias" valor={d.proximos} onClick={() => abrirLista('Próximos dias (pendentes)', pendentes('proximos'))} />
              <Numero rotulo={`${diaMes(dados.ontem.data)} (último dia útil) · % embalado no prazo`} valor={`${dados.ontem.pct}%`}
                cor={COR_SITUACAO[3]} onClick={() => void abrirOntem()} />
              {erroOntem && <Typography variant="caption" color="error">Não foi possível abrir a lista: {erroOntem}</Typography>}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
